// ================================================================
// STATE MANAGEMENT
// ================================================================
const STORAGE_KEY = "unicode-animator-project";
const STORAGE_PREFS_KEY = "unicode-animator-prefs";

export let state = {
  project: null,
  ui: {
    activeTab: "editor",
    selectedKeyframeId: null,
    selectedCell: null,
    selectedChar: null,
    unicodeSearch: "",
    openCategory: null,
    isPlaying: false,
    playbackStep: 0,
    playbackSpeed: 1,
    loop: true,
    activeSequenceIdx: 0,
    activeColorIndex: 0,
    editingPaletteSlot: null,
  }
};

export let prefs = {
  recent: [],
  favorites: [],
  palettes: [],
};

export const PALETTE_SIZE = 8;
export function emptyPalette() {
  return { colors: Array.from({ length: PALETTE_SIZE }, () => null) };
}

const listeners = new Set();
export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }
export function notify(what) {
  for (const fn of listeners) fn(what);
  debouncedSave();
}

let saveTimer = null;
let _cloudSaveHook = null;
export function setCloudSaveHook(fn) { _cloudSaveHook = fn; }

export function debouncedSave() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state.project));
    localStorage.setItem(STORAGE_PREFS_KEY, JSON.stringify(prefs));
    // Trigger cloud save if available
    if (_cloudSaveHook && state.project) _cloudSaveHook(state.project);
  }, 500);
}

// ================================================================
// UNDO / REDO
// ================================================================
const undoStack = [];
const redoStack = [];
const MAX_UNDO = 50;

export function pushUndo() {
  undoStack.push(JSON.stringify(state.project));
  if (undoStack.length > MAX_UNDO) undoStack.shift();
  redoStack.length = 0;
  renderUndoIndicator();
}

export function undo() {
  if (undoStack.length === 0) return;
  redoStack.push(JSON.stringify(state.project));
  state.project = JSON.parse(undoStack.pop());
  if (!state.project.keyframes[state.ui.selectedKeyframeId]) {
    const ids = Object.keys(state.project.keyframes);
    state.ui.selectedKeyframeId = ids[0] || null;
  }
  notify("undo");
  renderUndoIndicator();
}

export function redo() {
  if (redoStack.length === 0) return;
  undoStack.push(JSON.stringify(state.project));
  state.project = JSON.parse(redoStack.pop());
  if (!state.project.keyframes[state.ui.selectedKeyframeId]) {
    const ids = Object.keys(state.project.keyframes);
    state.ui.selectedKeyframeId = ids[0] || null;
  }
  notify("redo");
  renderUndoIndicator();
}

export function renderUndoIndicator() {
  const el = document.getElementById("undo-indicator");
  if (el) el.textContent = `undo: ${undoStack.length} | redo: ${redoStack.length}`;
}

// ================================================================
// DATA HELPERS
// ================================================================
export function makeId() { return "kf_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6); }

export function createEmptyGrid(rows, cols) {
  return Array.from({length: rows}, () =>
    Array.from({length: cols}, () => ({char: "", color: null}))
  );
}

export function createKeyframe(name, rows, cols) {
  const kf = {
    id: makeId(),
    name,
    rows,
    cols,
    grid: createEmptyGrid(rows, cols),
    defaultColor: "#ca4b15",
    backgroundColor: null,
    history: [],
    historyIndex: -1,
  };
  seedHistory(kf);
  return kf;
}

export function cloneKeyframe(kf) {
  const nk = JSON.parse(JSON.stringify(kf));
  nk.id = makeId();
  nk.name = kf.name + " copy";
  nk.history = [];
  nk.historyIndex = -1;
  seedHistory(nk);
  return nk;
}

// ================================================================
// PER-KEYFRAME HISTORY
// ================================================================
export const HISTORY_MAX = 30;
const STROKE_COLLAPSE_MS = 500;

function deepCloneGrid(g) {
  return g.map(row => row.map(c => ({ char: c.char, color: c.color })));
}

export function snapshotKf(kf) {
  return {
    rows: kf.rows,
    cols: kf.cols,
    name: kf.name,
    defaultColor: kf.defaultColor,
    backgroundColor: kf.backgroundColor,
    grid: deepCloneGrid(kf.grid),
  };
}

function cloneSnapshot(snap) {
  return {
    rows: snap.rows,
    cols: snap.cols,
    name: snap.name,
    defaultColor: snap.defaultColor,
    backgroundColor: snap.backgroundColor,
    grid: deepCloneGrid(snap.grid),
  };
}

function makeDiff(prev, next) {
  const diff = {};
  if (prev.rows !== next.rows || prev.cols !== next.cols) {
    diff.rows = next.rows;
    diff.cols = next.cols;
    diff.grid = deepCloneGrid(next.grid);
  } else {
    const cells = [];
    for (let r = 0; r < next.rows; r++) {
      for (let c = 0; c < next.cols; c++) {
        const a = prev.grid[r][c], b = next.grid[r][c];
        if (a.char !== b.char || a.color !== b.color) {
          cells.push([r, c, b.char, b.color]);
        }
      }
    }
    if (cells.length) diff.cells = cells;
  }
  if (prev.name !== next.name) diff.name = next.name;
  if (prev.defaultColor !== next.defaultColor) diff.defaultColor = next.defaultColor;
  if (prev.backgroundColor !== next.backgroundColor) diff.backgroundColor = next.backgroundColor;
  return diff;
}

function applyDiff(snap, diff) {
  if (diff.grid) {
    snap.grid = deepCloneGrid(diff.grid);
    snap.rows = diff.rows;
    snap.cols = diff.cols;
  } else if (diff.cells) {
    for (const [r, c, ch, col] of diff.cells) {
      snap.grid[r][c] = { char: ch, color: col };
    }
  }
  if ("name" in diff) snap.name = diff.name;
  if ("defaultColor" in diff) snap.defaultColor = diff.defaultColor;
  if ("backgroundColor" in diff) snap.backgroundColor = diff.backgroundColor;
  return snap;
}

export function materializeHistoryEntry(kf, index) {
  if (index < 0 || index >= kf.history.length) return null;
  let cur = cloneSnapshot(kf.history[0].snapshot);
  for (let i = 1; i <= index; i++) {
    applyDiff(cur, kf.history[i].diff);
  }
  return cur;
}

function applySnapshotToKf(kf, snap) {
  kf.grid = deepCloneGrid(snap.grid);
  kf.rows = snap.rows;
  kf.cols = snap.cols;
  kf.name = snap.name;
  kf.defaultColor = snap.defaultColor;
  kf.backgroundColor = snap.backgroundColor;
}

function seedHistory(kf) {
  kf.history = [{ ts: Date.now(), label: "created", snapshot: snapshotKf(kf) }];
  kf.historyIndex = 0;
}

let _historyConfirmHook = null;
export function setHistoryConfirmHook(fn) { _historyConfirmHook = fn; }

let _historyChangeHook = null;
export function setHistoryChangeHook(fn) { _historyChangeHook = fn; }
function fireHistoryChanged() { if (_historyChangeHook) _historyChangeHook(); }

export function recordKfHistory(kf, label, prevSnap) {
  if (!kf) return;
  if (!Array.isArray(kf.history) || kf.history.length === 0) seedHistory(kf);
  if (kf.historyIndex < kf.history.length - 1) {
    if (!_historyConfirmHook) {
      kf.history = kf.history.slice(0, kf.historyIndex + 1);
      appendHistoryEntry(kf, label, prevSnap);
      fireHistoryChanged();
      return;
    }
    _historyConfirmHook({
      onConfirm: () => {
        kf.history = kf.history.slice(0, kf.historyIndex + 1);
        appendHistoryEntry(kf, label, prevSnap);
        notify("history-record");
        fireHistoryChanged();
      },
      onCancel: () => {
        applySnapshotToKf(kf, prevSnap);
        notify("history-restore");
        fireHistoryChanged();
      },
    });
    return;
  }
  appendHistoryEntry(kf, label, prevSnap);
  fireHistoryChanged();
}

function appendHistoryEntry(kf, label, prevSnap) {
  const now = Date.now();
  const nextSnap = snapshotKf(kf);

  // Same-label collapse within 500ms — merge with the tip entry.
  const tipIdx = kf.history.length - 1;
  if (tipIdx >= 1) {
    const tip = kf.history[tipIdx];
    if (tip.label === label && now - tip.ts < STROKE_COLLAPSE_MS) {
      const base = materializeHistoryEntry(kf, tipIdx - 1);
      tip.diff = makeDiff(base, nextSnap);
      tip.ts = now;
      return;
    }
  }

  if (kf.history.length === 0) {
    kf.history.push({ ts: now, label, snapshot: nextSnap });
  } else {
    kf.history.push({ ts: now, label, diff: makeDiff(prevSnap, nextSnap) });
  }

  while (kf.history.length > HISTORY_MAX) {
    const removed = kf.history.shift();
    const next = kf.history[0];
    const newAnchor = applyDiff(cloneSnapshot(removed.snapshot), next.diff);
    kf.history[0] = { ts: next.ts, label: next.label, snapshot: newAnchor };
  }
  kf.historyIndex = kf.history.length - 1;
}

export function jumpToHistory(kf, targetIndex) {
  if (!kf) return;
  if (!Array.isArray(kf.history) || kf.history.length === 0) seedHistory(kf);
  if (targetIndex < 0 || targetIndex >= kf.history.length) return;
  const snap = materializeHistoryEntry(kf, targetIndex);
  applySnapshotToKf(kf, snap);
  kf.historyIndex = targetIndex;
  notify("history-jump");
  fireHistoryChanged();
}

export function keyframeToText(kf) {
  return kf.grid.map(row => row.map(c => c.char || " ").join("")).join("\n");
}

export function getSelectedKeyframe() {
  return state.project.keyframes[state.ui.selectedKeyframeId] || null;
}

export function getActiveSequence() {
  const idx = state.ui.activeSequenceIdx || 0;
  return state.project.sequences[idx] || null;
}

export function loadPrefs() {
  try {
    const saved = localStorage.getItem(STORAGE_PREFS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      prefs.recent = parsed.recent || [];
      prefs.favorites = parsed.favorites || [];
      prefs.palettes = parsed.palettes || [];
    }
  } catch(e) { /* ignore */ }
}

export function migrateProject(project) {
  if (!project) return project;
  if (!project.activePalette || !Array.isArray(project.activePalette.colors)) {
    project.activePalette = emptyPalette();
  } else if (project.activePalette.colors.length < PALETTE_SIZE) {
    while (project.activePalette.colors.length < PALETTE_SIZE) project.activePalette.colors.push(null);
  }
  if (project.keyframes) {
    for (const kf of Object.values(project.keyframes)) {
      if (!("backgroundColor" in kf)) kf.backgroundColor = null;
      if (!Array.isArray(kf.history) || kf.history.length === 0) {
        seedHistory(kf);
      } else if (typeof kf.historyIndex !== "number") {
        kf.historyIndex = kf.history.length - 1;
      }
    }
  }
  return project;
}

export function loadProject() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      state.project = migrateProject(JSON.parse(saved));
      return true;
    }
  } catch(e) { /* ignore */ }
  return false;
}
